/**
 * AreaSwitcher -- pinned-bottom area switcher for all sidebar variants.
 *
 * Renders a vertical list of NavLinks for Chat, Code, and Admin areas.
 * - Chat and Code are visible to all members.
 * - Admin is role-gated behind `maintainer`.
 * - Code is feature-flagged via VITE_CODE_PLATFORM_ENABLED.
 * - Active state highlights the current area based on pathname prefix.
 * - Collapsed mode renders icon-only buttons with title attributes.
 *
 * Used by AppSidebar's `areaSwitcher` slot.
 */

import { NavLink, useLocation } from 'react-router';
import { useIntl } from 'react-intl';
import { ChatCircleDots, Code, Shield } from '@phosphor-icons/react';
import type { ComponentType } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { useAuthStore } from '@/stores/useAuthStore';
import { hasMinimumRole } from '@/components/guards/RoleGuard';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type OrgRole = 'admin' | 'maintainer' | 'member';
type AreaKey = 'chat' | 'code' | 'admin';

interface AreaEntry {
  key: AreaKey;
  labelId: string;
  path: string;
  /** Prefix for active pathname match (/chat, /code, /admin). */
  prefix: string;
  icon: ComponentType<IconProps>;
  minimumRole: OrgRole;
  /** Optional runtime gate (e.g. feature flag). Return true to show. */
  featureFlag?: () => boolean;
}

// ---------------------------------------------------------------------------
// Area definitions
// ---------------------------------------------------------------------------

const AREAS: AreaEntry[] = [
  {
    key: 'chat',
    labelId: 'nav.area.chat',
    path: '/chat',
    prefix: '/chat',
    icon: ChatCircleDots,
    minimumRole: 'member',
  },
  {
    key: 'code',
    labelId: 'nav.area.code',
    path: '/code',
    prefix: '/code',
    icon: Code,
    minimumRole: 'member',
    featureFlag: () => import.meta.env.VITE_CODE_PLATFORM_ENABLED === 'true',
  },
  {
    key: 'admin',
    labelId: 'nav.area.admin',
    path: '/admin',
    prefix: '/admin',
    icon: Shield,
    minimumRole: 'maintainer',
  },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AreaSwitcherProps {
  /** When true, render icon-only buttons (matches AdminSidebar collapse). */
  collapsed?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AreaSwitcher({ collapsed = false }: AreaSwitcherProps) {
  const intl = useIntl();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  if (!user) return null;

  const visible = AREAS.filter(
    (a) =>
      hasMinimumRole(user.role, a.minimumRole) &&
      (a.featureFlag ? a.featureFlag() : true),
  );

  return (
    <ul role="list" className="flex flex-col gap-0.5">
      {visible.map((area) => {
        const Icon = area.icon;
        const isActive =
          location.pathname === area.path ||
          location.pathname.startsWith(area.prefix + '/') ||
          location.pathname === area.prefix;
        const label = intl.formatMessage({ id: area.labelId });

        const linkContent = (
          <NavLink
            to={area.path}
            aria-current={isActive ? 'page' : undefined}
            aria-label={label}
            title={collapsed ? label : undefined}
            className={cn(
              'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
              isActive
                ? 'bg-sidebar-item-active-bg text-sidebar-item-active-text'
                : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary',
              collapsed && 'justify-center px-0',
            )}
          >
            <Icon
              size={18}
              weight={isActive ? 'fill' : 'regular'}
              aria-hidden="true"
              className="shrink-0"
            />
            {!collapsed && <span className="truncate">{label}</span>}
          </NavLink>
        );

        return (
          <li key={area.key}>
            {collapsed ? (
              <Tooltip content={label} position="right">
                {linkContent}
              </Tooltip>
            ) : (
              linkContent
            )}
          </li>
        );
      })}
    </ul>
  );
}
